import React, { useEffect, useState } from "react";
import ZDrawer from "@molecules/ZDrawer";
import { MyVendor } from "@/Contracts/Marketplace";
import { getVendorsListPost } from "@/Services/Marketplace/Vendor";
import { getVendorCustomFields } from "@/Services/Settings/CustomFields";
import {
	CustomField,
	CustomFieldType
} from "@/Contracts/Settings";
import {
	Address,
	Client,
	ClientContact,
	ClientContactRequest,
	ListPagePostRequest,
	OpportunityVendorDetails,
	VendorSubscriptionStatus,
	ZomentumEntities
} from "@/Contracts";
import { useForm } from "antd/lib/form/Form";
import ZForm, {
	getCustomFieldProps,
	getWidget
} from "@organisms/ZForm";
import {
	EWidgets,
	IFormFieldProps,
	IFormProps,
	IFormSectionProps
} from "@organisms/ZForm/interface";
import "./index.less";
import { ZAlert, ZButton, ZTypography } from "@atoms";
import { Divider, Skeleton } from "antd";
import {
	ShareOpportunityWithVendorRequest,
	vendorClientContactFieldsCommonWithGrow,
	vendorClientFieldsCommonWithGrow,
	vendorOpportunityFieldsCommonWithGrow
} from "@/Contracts";
import { useComponentDidUpdate, useFeatureFlag } from "@/Hooks";
import {
	capitalizeAfterUnderscore,
	getShippingAddressRowText,
	processCustomFieldFormValues
} from "@/Utils/common";
import moment, { Moment } from "moment";
import ContactSelector from "./ContactSelector";
import { getClientContacts, getClientDetails } from "@/Services/Clients";
import { normalizeZipcode } from "@/V2Utils";

interface Props {
	readonly visible: boolean;
	readonly onClose: () => void;
	readonly onShareOpportunity: (
		shareOpportunityWithVendorRequest: ShareOpportunityWithVendorRequest
	) => Promise<void>;
	readonly opportunityVendorDetails?: OpportunityVendorDetails | null;
	readonly clientContactList: ClientContact[];
	readonly selectedPipelineVendorId: string | null;
	readonly estimatedValue?: number;
	readonly updateEstimatedValue: (estimatedValue: number) => void;
	readonly estimatedCloseDate?: Moment | null;
	readonly updateEstimatedCloseDate: (estimatedCloseDate: Moment) => void;
	readonly selectedClient?: string | null;
	readonly selectedPrimaryContact?: string | null;
	readonly updateSelectedPrimaryContact: (
		selectedPrimaryContact: string
	) => void;
	readonly selectedLinkedContacts?: string[];
	readonly updateSelectedLinkedContacts: (
		selectedLinkedContacts: string[]
	) => void;
	readonly isOnboardingClientSelected?: boolean;
}

const ShareOpportunityWithVendorDrawer: React.FC<Props> = props => {
	const isMarketplacePipelineSyncingEnabled = useFeatureFlag(
		"marketplace_pipeline_syncing"
	);
	const selectedContactIds = [props.selectedPrimaryContact ?? []]
		.flat()
		.concat(props.selectedLinkedContacts ?? []);

	const [recentlySelectedId, setRecentlySelectedId] = useState<string | null>(
		null
	);
	const [selectedVendor, setSelectedVendor] = useState<MyVendor | null>(null);
	const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingClientDetails, setIsLoadingClientDetails] = useState(false);
	const [isLoadingClientContacts, setIsLoadingClientContacts] =
		useState(false);
	const [isLoadingCustomFields, setIsLoadingCustomFields] = useState(false);
	const [shareOpportunityWithVendorForm] = useForm();
	const [customFields, setCustomFields] = useState<CustomField[]>([]);
	const [vendorList, setVendorList] = useState<MyVendor[]>([]);
	const [
		emptyClientSystemFieldsCommonWithGrow,
		setEmptyClientSystemFieldsCommonWithGrow
	] = useState<string[]>([]);
	const [
		emptyClientContactSystemFieldsCommonWithGrow,
		setEmptyClientContactSystemFieldsCommonWithGrow
	] = useState<Record<string, string[]>>({});

	const showPartnerAlignFeaturesFlag = useFeatureFlag(
		"show_partner_align_features"
	);

	const getMyVendorsData = async () => {
		try {
			setIsLoading(true);
			const response = await getVendorsListPost(
				new ListPagePostRequest({
					count_query_flag: true,
					filter_criterion: [],
					sort_criterion: [],
					page_no: 1,
					page_size: 50,
					includedChildEntities: []
				}),
				true,
				false
			);
			setVendorList(response.data);
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};
	useEffect(() => {
		if (props.visible && showPartnerAlignFeaturesFlag) {
			getMyVendorsData();
		}
	}, [props.visible]);
	const handleVendorSelect = async (vendorId: string) => {
		try {
			const selectedVendor = vendorList.find(
				vendor => vendor.id === vendorId
			);
			selectedVendor && setSelectedVendor(selectedVendor);
			setIsSubmitDisabled(true);
			setIsLoadingCustomFields(true);
			const customFieldsResponse = await getVendorCustomFields(vendorId);
			setCustomFields(customFieldsResponse.data);
		} catch (error) {
			console.error(error);
		} finally {
			setIsSubmitDisabled(false);
			setIsLoadingCustomFields(false);
		}
	};
	const vendorOpportunityCustomFields = customFields.filter(
		customField =>
			customField.entity_type === ZomentumEntities.VendorOpportunity
	);

	const vendorClientCustomFields = customFields.filter(
		customField => customField.entity_type === ZomentumEntities.VendorClient
	);

	const vendorClientContactCustomFields = customFields.filter(
		customField =>
			customField.entity_type === ZomentumEntities.VendorClientContact
	);
	const getFormSections = () => {
		const ExtraInfoVendorSelect = () =>
			isMarketplacePipelineSyncingEnabled &&
			!!selectedVendor &&
			props.selectedPipelineVendorId !== selectedVendor.id ? (
				<ZAlert
					message={`Sharing this opportunity with ${
						selectedVendor?.name ?? "vendor"
					}, will create it in their pipeline.`}
					showIcon
				/>
			) : null;
		const formSections: IFormSectionProps[] = [
			{
				isIndentFormItems: false,
				meta: {
					formItemLayout: [5, 19],
					fields: [
						{
							key: "vendor_company_id",
							label: "Vendor",
							required: true,
							rules: [
								{
									whitespace: true,
									message: "Enter field name"
								}
							],
							message: "Please select vendor",
							widget: EWidgets.SELECT,
							initialValue:
								props.selectedPipelineVendorId ?? undefined,
							widgetProps: {
								width: "full",
								placeholder: "Please select vendor",
								selectOptions: vendorList.map(vendor => ({
									key: vendor.id,
									name: vendor.name,
									value: vendor.id,
									disabled:
										vendor.status !==
										VendorSubscriptionStatus.ACCEPTED
								})),
								onChange: handleVendorSelect
							},
							extra: <ExtraInfoVendorSelect />
						}
					]
				}
			}
		];

		if (!!vendorOpportunityCustomFields.length) {
			const vendorOpportunityCustomFieldsSection: IFormSectionProps = {
				title: "Opportunity Fields",
				meta: {
					formItemLayout: [5, 19],
					fields: vendorOpportunityCustomFields.map(customField => {
						const widgetProps: Record<string, any> =
							getCustomFieldProps(customField);
						if (
							customField.system_field_id ===
							"vendor_opportunity_primary_client_user_id"
						) {
							return {
								key: customField.id,
								label: customField.display_name,
								required:
									!!customField.partner_sharing_details
										?.mandatory_for_opp_sharing,
								widget: ContactSelector,
								widgetProps: {
									fieldId: "primary_client_user_id",
									selectedClient: props.selectedClient,
									selectedPrimaryClientContact:
										props.selectedPrimaryContact,
									selectedLinkedClientContacts:
										props.selectedLinkedContacts,
									onChange: (value: string) => {
										props.updateSelectedPrimaryContact(
											value
										);
										fetchClientContacts();
									},
									onSelect: (value: string) => {
										setRecentlySelectedId(value);
										fetchClientContacts();
									},
									isOnboardingClientSelected:
										props.isOnboardingClientSelected
								},
								className: "large-custom-field"
							};
						} else if (
							customField.system_field_id ===
							"vendor_opportunity_linked_client_user_ids"
						) {
							return {
								key: customField.id,
								label: customField.display_name,
								required:
									!!customField.partner_sharing_details
										?.mandatory_for_opp_sharing,
								widget: ContactSelector,
								widgetProps: {
									fieldId: "linked_client_user_ids",
									onChange: (value: string[]) => {
										props.updateSelectedLinkedContacts(
											value
										);
										fetchClientContacts();
									},
									onSelect: (value: string) => {
										setRecentlySelectedId(value);
										fetchClientContacts();
									},
									isOnboardingClientSelected:
										props.isOnboardingClientSelected,
									selectedClient: props.selectedClient,
									selectedPrimaryClientContact:
										props.selectedPrimaryContact,
									selectedLinkedClientContacts:
										props.selectedLinkedContacts
								},
								className: "large-custom-field"
							};
						} else if (
							customField.system_field_id ===
							"vendor_opportunity_estimated_value"
						)
							widgetProps.onChange = props.updateEstimatedValue;
						else if (
							customField.system_field_id ===
							"vendor_opportunity_estimated_close_date"
						) {
							widgetProps.onChange =
								props.updateEstimatedCloseDate;
						}
						return {
							key: customField.id,
							label: customField.display_name,
							required:
								!!customField.partner_sharing_details
									?.mandatory_for_opp_sharing,
							message: `Please enter ${customField.display_name}`,
							widget: getWidget(customField.field_type),
							widgetProps,
							className: "large-custom-field"
						};
					})
				}
			};
			formSections.push(vendorOpportunityCustomFieldsSection);
		}

		if (!!vendorClientCustomFields.length) {
			const clientFields = vendorClientCustomFields.filter(
				field =>
					!field.system_field_id ||
					emptyClientSystemFieldsCommonWithGrow.includes(
						field.system_field_id
					)
			);
			if (!!clientFields.length) {
				const vendorClientCustomFieldsSection: IFormSectionProps = {
					title: "Client Fields",
					meta: {
						formItemLayout: [5, 19],
						fields: clientFields
							.map(customField => {
								if (
									customField.system_field_id ===
									"vendor_client_billing_address"
								) {
									const addressFieldsArray = [
										"vendor_client_address_line_1",
										"vendor_client_address_line_2",
										"vendor_client_city",
										"vendor_client_state",
										"vendor_client_pincode",
										"vendor_client_country"
									];
									return [
										{
											key: "billing_address",
											wrapperCol: { span: 24 },
											render: () => (
												<React.Fragment key="billing_address">
													<Divider />
													<ZTypography.ZSectionHeading className="block my-4">
														Billing Address
													</ZTypography.ZSectionHeading>
												</React.Fragment>
											)
										},
										...addressFieldsArray.map(entity => ({
											key: entity,
											label:
												entity === "pincode"
													? "Zip Code"
													: entity ===
													  "address_line_1"
													? "Street"
													: entity ===
													  "address_line_2"
													? "Street 2"
													: capitalizeAfterUnderscore(
															entity.replace(
																"vendor_client_",
																""
															)
													  ),
											hasFeedback: true,
											widget: EWidgets.INPUT,
											normalize:
												entity === "pincode"
													? normalizeZipcode
													: undefined,
											widgetProps: {
												width: "full",
												placeholder: `Please enter ${
													entity === "pincode"
														? "zip code"
														: entity ===
														  "address_line_1"
														? "Street"
														: entity ===
														  "address_line_2"
														? "Street 2"
														: `${entity} name`
												}`
											}
										})),
										{
											key: "billing_address_end",
											wrapperCol: { span: 24 },
											render: () => (
												<React.Fragment key="billing_address_end">
													<Divider className="mb-6" />
												</React.Fragment>
											)
										}
									];
								}
								return {
									key: customField.id,
									label: customField.display_name,
									required:
										!!customField.partner_sharing_details
											?.mandatory_for_opp_sharing,
									message: `Please enter ${customField.display_name}`,
									widget: getWidget(customField.field_type),
									widgetProps:
										getCustomFieldProps(customField),
									className: "large-custom-field"
								};
							})
							.flat()
					}
				};
				formSections.push(vendorClientCustomFieldsSection);
			}
		}

		if (!!vendorClientContactCustomFields.length && !!selectedContactIds) {
			selectedContactIds.forEach(clientContactId => {
				const clientContact = clientContacts?.find(
					contact => contact.id === clientContactId
				);
				const clientContactFields =
					vendorClientContactCustomFields.filter(
						field =>
							!field.system_field_id ||
							emptyClientContactSystemFieldsCommonWithGrow[
								clientContactId
							]?.includes(field.system_field_id)
					);
				if (!!clientContactFields.length) {
					const vendorClientContactCustomFieldsSection: IFormSectionProps =
						{
							title: `Contact Fields for ${clientContact?.name.full}`,
							meta: {
								formItemLayout: [5, 19],
								fields: clientContactFields.map(customField => {
									const field: IFormFieldProps = {
										key: `${clientContactId}-${customField.id}`,
										label: customField.display_name,
										required:
											!!customField
												.partner_sharing_details
												?.mandatory_for_opp_sharing,
										message: `Please enter ${customField.display_name}`,
										widget: getWidget(
											customField.field_type
										),
										widgetProps:
											getCustomFieldProps(customField),
										className: "large-custom-field"
									};
									if (
										customField.system_field_id ===
										"vendor_client_user_email"
									) {
										if (field.widgetProps)
											field.widgetProps.type = "email";
										field.rules = [
											{
												type: "email",
												message:
													"Please enter valid email"
											}
										];
									}
									return field;
								})
							}
						};
					formSections.push(vendorClientContactCustomFieldsSection);
				}
			});
		}

		return formSections;
	};

	const [clientDetails, setClientDetails] = useState<Client>();
	const [clientContacts, setClientContacts] = useState<ClientContact[]>();

	const fetchClientDetails = async () => {
		if (props.selectedClient) {
			try {
				setIsLoadingClientDetails(true);
				const clientSystemFields = vendorClientCustomFields.filter(
					field =>
						vendorClientFieldsCommonWithGrow.includes(
							field.system_field_id ?? ""
						)
				);
				if (!!clientSystemFields.length) {
					const clientDetails = await getClientDetails(
						props.selectedClient
					);
					setClientDetails(clientDetails);
					const emptyClientSystemFields: string[] = [];
					if (!clientDetails.billing_address?.phone) {
						emptyClientSystemFields.push("vendor_client_phone");
					}
					const isBillingAddressRowTextBlank =
						clientDetails?.billing_address
							? !getShippingAddressRowText(
									clientDetails.billing_address
							  )
							: true;
					if (isBillingAddressRowTextBlank) {
						emptyClientSystemFields.push(
							"vendor_client_billing_address"
						);
					}
					if (!clientDetails.fax) {
						emptyClientSystemFields.push("vendor_client_fax");
					}
					if (!clientDetails.website) {
						emptyClientSystemFields.push("vendor_client_website");
					}
					if (!clientDetails.linked_in_url) {
						emptyClientSystemFields.push(
							"vendor_client_linked_in_url"
						);
					}
					if (!clientDetails.facebook_url) {
						emptyClientSystemFields.push(
							"vendor_client_facebook_url"
						);
					}
					if (!clientDetails.twitter_url) {
						emptyClientSystemFields.push(
							"vendor_client_twitter_url"
						);
					}
					setEmptyClientSystemFieldsCommonWithGrow(
						emptyClientSystemFields
					);
				}
			} catch (error) {
				console.error(error);
			} finally {
				setIsLoadingClientDetails(false);
			}
		}
	};

	const fetchClientContacts = async () => {
		if (props.selectedClient) {
			try {
				setIsLoadingClientContacts(true);
				const clientContactSystemFields =
					vendorClientContactCustomFields.filter(field =>
						vendorClientContactFieldsCommonWithGrow.includes(
							field.system_field_id ?? ""
						)
					);
				if (!!clientContactSystemFields.length) {
					const clientContactRequest = new ClientContactRequest(
						props.selectedClient
					);
					const clientContactsResponse = await getClientContacts(
						clientContactRequest
					);
					setClientContacts(clientContactsResponse.data);
					const emptyClientContactSystemFieldsData: Record<
						string,
						string[]
					> = {};
					clientContactsResponse.data.forEach(clientContact => {
						const emptyClientContactSystemFields: string[] = [];
						if (!clientContact.email) {
							emptyClientContactSystemFields.push(
								"vendor_client_user_email"
							);
						}
						if (!clientContact.phone) {
							emptyClientContactSystemFields.push(
								"vendor_client_user_phone"
							);
						}
						if (!clientContact.mobile) {
							emptyClientContactSystemFields.push(
								"vendor_client_user_mobile"
							);
						}
						if (!clientContact.job_title) {
							emptyClientContactSystemFields.push(
								"vendor_client_user_job_title"
							);
						}
						if (!clientContact.department) {
							emptyClientContactSystemFields.push(
								"vendor_client_user_department"
							);
						}
						emptyClientContactSystemFieldsData[clientContact.id] =
							emptyClientContactSystemFields;
					});
					setEmptyClientContactSystemFieldsCommonWithGrow(
						emptyClientContactSystemFieldsData
					);
				}
			} catch (error) {
				console.error(error);
			} finally {
				setIsLoadingClientContacts(false);
			}
		}
	};

	const getClientSystemFields = (
		customFields: CustomField[],
		formValues: Record<string, string>
	) => {
		if (!customFields.length) return null;
		const clientSystemFields: Partial<Client> = {};
		const billingAddress = new Address(
			clientDetails?.billing_address?.address_line_1,
			clientDetails?.billing_address?.address_line_2,
			clientDetails?.billing_address?.city,
			clientDetails?.billing_address?.country,
			clientDetails?.billing_address?.phone,
			clientDetails?.billing_address?.pincode,
			clientDetails?.billing_address?.state
		);
		clientSystemFields.billing_address = billingAddress;
		customFields.forEach(field => {
			if (
				[
					"vendor_client_fax",
					"vendor_client_website",
					"vendor_client_linked_in_url",
					"vendor_client_facebook_url",
					"vendor_client_twitter_url"
				].includes(field.system_field_id ?? "")
			) {
				const fieldName = field.system_field_id?.replace(
					"vendor_client_",
					""
				);
				if (fieldName)
					clientSystemFields[fieldName] = formValues[field.id];
			} else if (field.system_field_id === "vendor_client_phone") {
				if (clientSystemFields.billing_address)
					clientSystemFields.billing_address.phone = `${
						formValues[field.id]
					}`;
			} else if (
				field.system_field_id === "vendor_client_billing_address"
			) {
				if (clientSystemFields.billing_address) {
					clientSystemFields.billing_address.address_line_1 =
						formValues["vendor_client_address_line_1"];
					clientSystemFields.billing_address.address_line_2 =
						formValues["vendor_client_address_line_2"];
					clientSystemFields.billing_address.city =
						formValues["vendor_client_city"];
					clientSystemFields.billing_address.state =
						formValues["vendor_client_state"];
					clientSystemFields.billing_address.pincode =
						formValues["vendor_client_pincode"];
					clientSystemFields.billing_address.country =
						formValues["vendor_client_country"];
				}
			}
		});
		return {
			client_company_id: props.selectedClient ?? "",
			client_company_system_fields: clientSystemFields
		};
	};

	const getClientContactsSystemFields = (
		customFields: CustomField[],
		formValues: Record<string, string>
	) => {
		if (!customFields.length) return null;
		const clientContactsSystemFields: {
			client_contact_id: string;
			client_contact_system_fields: Partial<ClientContact>;
		}[] = [];
		selectedContactIds.forEach(contactId => {
			const clientContact: Partial<ClientContact> = {};
			customFields.forEach(field => {
				const fieldName = field.system_field_id?.replace(
					"vendor_client_user_",
					""
				);
				if (fieldName)
					clientContact[fieldName] = `${
						formValues[`${contactId}-${field.id}`]
					}`;
			});
			clientContactsSystemFields.push({
				client_contact_id: contactId,
				client_contact_system_fields: clientContact
			});
		});
		return clientContactsSystemFields;
	};

	const onShare = async (formValues: Record<string, string>) => {
		try {
			setIsLoading(true);
			const vendorOpportunityFields = processCustomFieldFormValues(
				formValues,
				vendorOpportunityCustomFields.filter(
					field =>
						!vendorOpportunityFieldsCommonWithGrow.includes(
							field.system_field_id ?? ""
						)
				)
			);

			const vendorClientCompanyFields = processCustomFieldFormValues(
				formValues,
				vendorClientCustomFields.filter(
					field =>
						!vendorClientFieldsCommonWithGrow.includes(
							field.system_field_id ?? ""
						)
				)
			);

			const vendorClientUserFields = props.clientContactList.map(
				clientContact => {
					const vendorClientUserField = {
						client_user_id: clientContact.id,
						custom_fields: processCustomFieldFormValues(
							formValues,
							vendorClientContactCustomFields.filter(
								field =>
									!vendorClientContactFieldsCommonWithGrow.includes(
										field.system_field_id ?? ""
									)
							)
						)
					};
					return vendorClientUserField;
				}
			);

			const shareOpportunityWithVendorRequest: ShareOpportunityWithVendorRequest =
				{
					vendor_company_id: formValues["vendor_company_id"],
					vendor_custom_fields: {
						vendor_opportunity_fields: vendorOpportunityFields,
						vendor_client_company_fields: vendorClientCompanyFields,
						vendor_client_user_fields: vendorClientUserFields
					},
					client_system_fields: getClientSystemFields(
						vendorClientCustomFields.filter(field =>
							vendorClientFieldsCommonWithGrow.includes(
								field.system_field_id ?? ""
							)
						),
						formValues
					),
					client_contact_system_fields: getClientContactsSystemFields(
						vendorClientContactCustomFields.filter(field =>
							vendorClientContactFieldsCommonWithGrow.includes(
								field.system_field_id ?? ""
							)
						),
						formValues
					)
				};
			await props.onShareOpportunity(shareOpportunityWithVendorRequest);
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const getInitialFormValues = () => {
		if (props.opportunityVendorDetails) {
			const formInitialValues: Record<
				string,
				string | Moment | undefined | number | string[]
			> = {};
			vendorOpportunityCustomFields.forEach(field => {
				if (
					!vendorOpportunityFieldsCommonWithGrow
						.concat(vendorClientFieldsCommonWithGrow)
						.concat(vendorClientContactFieldsCommonWithGrow)
						.includes(field.system_field_id ?? "")
				) {
					const isDateCustomField =
						field.field_type === CustomFieldType.DateField;
					const customField =
						props.opportunityVendorDetails?.vendor_custom_fields?.vendor_opportunity_fields.find(
							customField => customField.id === field.id
						);
					const initialValue = customField?.values
						? isDateCustomField && customField.values[0]
							? moment(customField.values[0])
							: customField.values[0]
						: undefined;
					formInitialValues[field.id] = initialValue;
				}
			});
			vendorClientCustomFields.forEach(field => {
				const isDateCustomField =
					field.field_type === CustomFieldType.DateField;
				const customField =
					props.opportunityVendorDetails?.vendor_custom_fields?.vendor_client_company_fields.find(
						customField => customField.id === field.id
					);
				const initialValue = customField?.values
					? isDateCustomField && customField.values[0]
						? moment(customField.values[0])
						: customField.values[0]
					: undefined;
				formInitialValues[field.id] = initialValue;
			});
			props.opportunityVendorDetails?.vendor_custom_fields?.vendor_client_user_fields.forEach(
				clientContact => {
					vendorClientContactCustomFields.forEach(field => {
						const isDateCustomField =
							field.field_type === CustomFieldType.DateField;
						const customField = clientContact.custom_fields.find(
							customField => customField.id === field.id
						);
						const initialValue = customField?.values
							? isDateCustomField && customField.values[0]
								? moment(customField.values[0])
								: customField.values[0]
							: undefined;
						formInitialValues[field.id] = initialValue;
					});
				}
			);
			shareOpportunityWithVendorForm.setFieldsValue(formInitialValues);
		}
		fetchClientDetails();
		fetchClientContacts();
	};

	const getInitialFormValuesForCommonFields = () => {
		const formInitialValues: Record<
			string,
			string | Moment | undefined | number | string[]
		> = {};
		vendorOpportunityCustomFields.forEach(field => {
			if (
				field.system_field_id === "vendor_opportunity_estimated_value"
			) {
				formInitialValues[field.id] = props.estimatedValue;
			} else if (
				field.system_field_id ===
				"vendor_opportunity_estimated_close_date"
			) {
				formInitialValues[field.id] =
					props.estimatedCloseDate ?? undefined;
			} else if (
				field.system_field_id ===
				"vendor_opportunity_primary_client_user_id"
			) {
				formInitialValues[field.id] =
					props.selectedPrimaryContact ?? undefined;
			} else if (
				field.system_field_id ===
				"vendor_opportunity_linked_client_user_ids"
			) {
				formInitialValues[field.id] =
					props.selectedLinkedContacts ?? [];
			}
		});
		shareOpportunityWithVendorForm.setFieldsValue(formInitialValues);
	};

	useComponentDidUpdate(() => {
		const formInitialValues: Record<
			string,
			string | Moment | undefined | number | string[]
		> = {};
		vendorClientCustomFields.forEach(field => {
			if (field.system_field_id === "vendor_client_phone") {
				formInitialValues[field.id] =
					clientDetails?.billing_address?.phone;
			} else if (
				field.system_field_id === "vendor_client_billing_address"
			) {
				formInitialValues["vendor_client_address_line_1"] =
					clientDetails?.billing_address?.address_line_1;
				formInitialValues["vendor_client_address_line_2"] =
					clientDetails?.billing_address?.address_line_2;
				formInitialValues["vendor_client_city"] =
					clientDetails?.billing_address?.city;
				formInitialValues["vendor_client_state"] =
					clientDetails?.billing_address?.state;
				formInitialValues["vendor_client_pincode"] =
					clientDetails?.billing_address?.pincode;
				formInitialValues["vendor_client_country"] =
					clientDetails?.billing_address?.country;
			} else if (field.system_field_id === "vendor_client_fax") {
				formInitialValues[field.id] = clientDetails?.fax;
			} else if (field.system_field_id === "vendor_client_website") {
				formInitialValues[field.id] = clientDetails?.website;
			} else if (
				field.system_field_id === "vendor_client_linked_in_url"
			) {
				formInitialValues[field.id] =
					clientDetails?.linked_in_url ?? undefined;
			} else if (field.system_field_id === "vendor_client_facebook_url") {
				formInitialValues[field.id] =
					clientDetails?.facebook_url ?? undefined;
			} else if (field.system_field_id === "vendor_client_twitter_url") {
				formInitialValues[field.id] =
					clientDetails?.twitter_url ?? undefined;
			}
		});
		shareOpportunityWithVendorForm.setFieldsValue(formInitialValues);
	}, [clientDetails]);

	useComponentDidUpdate(() => {
		const formInitialValues: Record<
			string,
			string | Moment | undefined | number | string[]
		> = {};
		clientContacts?.forEach(clientContact => {
			if (
				!!recentlySelectedId
					? recentlySelectedId === clientContact.id
					: clientContact.id === props.selectedPrimaryContact ||
					  props.selectedLinkedContacts?.includes(clientContact.id)
			) {
				vendorClientContactCustomFields.forEach(field => {
					if (field.system_field_id === "vendor_client_user_email") {
						formInitialValues[`${clientContact.id}-${field.id}`] =
							clientContact.email ?? undefined;
					} else if (
						field.system_field_id === "vendor_client_user_phone"
					) {
						formInitialValues[`${clientContact.id}-${field.id}`] =
							clientContact.phone;
					} else if (
						field.system_field_id === "vendor_client_user_mobile"
					) {
						formInitialValues[`${clientContact.id}-${field.id}`] =
							clientContact.mobile;
					} else if (
						field.system_field_id === "vendor_client_user_job_title"
					) {
						formInitialValues[`${clientContact.id}-${field.id}`] =
							clientContact.job_title;
					} else if (
						field.system_field_id ===
						"vendor_client_user_department"
					) {
						formInitialValues[`${clientContact.id}-${field.id}`] =
							clientContact.department;
					}
				});
			}
		});
		shareOpportunityWithVendorForm.setFieldsValue(formInitialValues);
		setRecentlySelectedId(null);
	}, [clientContacts]);

	useComponentDidUpdate(() => {
		if (props.visible && !!customFields.length) {
			getInitialFormValues();
			getInitialFormValuesForCommonFields();
		}
	}, [customFields, props.visible]);
	useComponentDidUpdate(() => {
		if (props.visible && !!customFields.length) {
			getInitialFormValuesForCommonFields();
		}
	}, [props.selectedPrimaryContact, props.selectedLinkedContacts]);

	useComponentDidUpdate(() => {
		if (props.visible)
			shareOpportunityWithVendorForm.setFieldsValue({
				vendor_company_id: props.selectedPipelineVendorId
			});
	}, [props.visible]);

	useComponentDidUpdate(() => {
		if (props.visible && props.selectedPipelineVendorId) {
			handleVendorSelect(props.selectedPipelineVendorId);
		}
	}, [props.visible, props.opportunityVendorDetails, vendorList]);

	const formProps: IFormProps = {
		form: shareOpportunityWithVendorForm,
		sections: getFormSections(),
		key: "ShareOpportunityWithVendorForm",
		layout: "horizontal",
		isSubmitAllowed: false,
		onFinish: onShare
	};

	return (
		<ZDrawer
			className="share-opportunity-with-vendor-drawer"
			title="Share Opportunity with Vendor"
			visible={props.visible}
			onClose={props.onClose}
			footer={[
				<ZButton
					key="cancel-button"
					className="mr-4"
					onClick={props.onClose}
				>
					Cancel
				</ZButton>,
				<ZButton
					key="share-button"
					disabled={isSubmitDisabled}
					loading={
						isLoading ||
						isLoadingCustomFields ||
						isLoadingClientDetails ||
						isLoadingClientContacts
					}
					type="primary"
					onClick={shareOpportunityWithVendorForm.submit}
				>
					Share
				</ZButton>
			]}
		>
			<Skeleton
				loading={
					isLoading ||
					isLoadingCustomFields ||
					isLoadingClientDetails ||
					isLoadingClientContacts
				}
			>
				<ZForm {...formProps} />
			</Skeleton>
		</ZDrawer>
	);
};

export default ShareOpportunityWithVendorDrawer;
